/**
 * Shared UI helpers for the admin invite sections (single + master).
 * Extracted so InvitesSection and MasterInvitesSection don't duplicate
 * origin resolution, link building, and copy/format utilities.
 */
import { toast } from "sonner";

// Invite links should always live on the root brand domain regardless of
// which subdomain the admin console is served from (admin might be on
// switch.offer.express or loans.offer.express — the invite URL should still
// be `https://offer.express/i/<slug>`). Configure via `REACT_APP_INVITE_ORIGIN`
// in production. Falls back to `window.location.origin` in preview/dev so
// invite links stay clickable there.
function _resolveInviteOrigin() {
  const explicit = process.env.REACT_APP_INVITE_ORIGIN || process.env.REACT_APP_PUBLIC_ORIGIN;
  if (explicit) return explicit;
  if (typeof window === "undefined") return "https://offer.express";
  const host = window.location.hostname;
  // In production, any *.offer.express subdomain must emit links on the root
  // domain. Preview / localhost / other hosts keep their own origin so the
  // links resolve during testing.
  if (host === "offer.express" || host.endsWith(".offer.express")) {
    return `${window.location.protocol}//offer.express`;
  }
  return window.location.origin;
}

export const PUBLIC_ORIGIN = _resolveInviteOrigin();

export function inviteUrl(slug) {
  return `${PUBLIC_ORIGIN}/i/${slug}`;
}

/** Master-invite link. style "path" → /m/<slug>/<tag>; "query" → /m/<slug>?v=<tag>.
 *  Both resolve to the same landing — admin picks whichever looks better in
 *  the blast message. Without a tag both return the bare base link. */
export function masterUrl(slug, tag, style = "path") {
  const base = `${PUBLIC_ORIGIN}/m/${slug}`;
  const t = (tag || "").trim();
  if (!t) return base;
  const enc = encodeURIComponent(t);
  return style === "query" ? `${base}?v=${enc}` : `${base}/${enc}`;
}

export function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diffS = Math.max(0, (Date.now() - then) / 1000);
  if (diffS < 60) return "just now";
  if (diffS < 3600) return `${Math.floor(diffS / 60)}m ago`;
  if (diffS < 86400) return `${Math.floor(diffS / 3600)}h ago`;
  return `${Math.floor(diffS / 86400)}d ago`;
}

export function copyToClipboard(text) {
  if (!text) return;
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  } catch {
    toast.error("Could not copy — long-press to select instead");
  }
}
